import { useTranslation } from 'react-i18next';
import { StatusPill } from '@ui-kit';
import { MONITORING_ENDPOINTS, MONITORING_INTERNAL_ONLY } from '../data/monitoringEndpoints';
import { useMonitoringCredentials } from '../hooks/useMonitoringCredentials';
import { useHasRole } from '../../_shared/hooks/useCurrentUser';

/**
 * #339/#341/#342/#385/#386/#654 — Operations card showing Grafana monitoring link + viewer credentials.
 * Grafana: visible to any logged-in user (operator decision — the exposed Grafana account is read-only
 * Viewer, with admin/config menus hidden, so showing it to all authenticated users is acceptable).
 * Since #654 the link is a same-domain nginx sub-path proxy (`/grafana/`), so mixed-content and SG
 * reachability no longer gate visibility.
 * Prometheus: removed from general view (internal-only, R13 #341); #654 restores it as a
 * ROLE_ADMIN-only entry tagged with an "internal network only" badge (raw query tool, SG-restricted).
 *
 * #385: Grafana card restored with label + description.
 * #386: Viewer credentials box fetched from BE.
 * #654: Grafana → relative `/grafana/` path · Prometheus admin-only entry + StatusPill badge.
 */
export function MonitoringLinks() {
  const { t } = useTranslation();
  const { data: credentials } = useMonitoringCredentials();
  const isAdmin = useHasRole('ROLE_ADMIN');

  const cards = [
    {
      id: 'grafana',
      label: t('_index.monitoring.grafana_label'),
      description: t('_index.monitoring.grafana_description'),
      url: MONITORING_ENDPOINTS.grafana,
      internalOnly: !!MONITORING_INTERNAL_ONLY.grafana,
    },
    ...(isAdmin
      ? [
          {
            id: 'prometheus',
            label: t('_index.monitoring.prometheus_label'),
            description: t('_index.monitoring.prometheusAdminNote'),
            url: MONITORING_ENDPOINTS.prometheus,
            internalOnly: !!MONITORING_INTERNAL_ONLY.prometheus,
          },
        ]
      : []),
  ];

  return (
    <div
      aria-label={t('_index.monitoring.title')}
      style={{
        background: 'var(--color-surface-muted)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--spacing-4)',
        marginTop: 'var(--spacing-4)',
      }}
    >
      <h2
        style={{
          margin: '0 0 var(--spacing-3) 0',
          fontSize: 'var(--font-size-base)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
        }}
      >
        {t('_index.monitoring.title')}
      </h2>

      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-3)',
        }}
      >
        {cards.map(({ id, label, description, url, internalOnly }) => (
          <li key={id}>
            {/* Card wrapper */}
            <div
              style={{
                padding: 'var(--spacing-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-surface-border)',
                background: 'var(--color-surface)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--spacing-2)',
              }}
            >
              {/* Link row */}
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${label}${
                  internalOnly ? ' — ' + t('_index.monitoring.internalOnlyBadge') : ''
                } — ${url} (${t('_index.monitoring.externalIcon')})`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  color: 'var(--color-info)',
                  textDecoration: 'none',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--spacing-2)',
                      fontWeight: 600,
                      color: 'var(--color-text-primary)',
                    }}
                  >
                    {label}
                    {internalOnly && <StatusPill tone="neutral">{t('_index.monitoring.internalOnlyBadge')}</StatusPill>}
                  </span>
                  <span
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {description}
                  </span>
                </div>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-1)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-muted)',
                    flexShrink: 0,
                    marginLeft: 'var(--spacing-3)',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--font-family-mono)',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {url}
                  </span>
                  <span aria-hidden="true">&#x2197;</span>
                  <span className="sr-only">{t('_index.monitoring.externalIcon')}</span>
                </span>
              </a>

              {/* #386 — Viewer credentials box (admin only, shown when credentials available) */}
              {id === 'grafana' && credentials?.grafana?.viewerUsername && (
                <div
                  role="region"
                  aria-label={t('_index.viewer.label')}
                  style={{
                    marginTop: 'var(--spacing-1)',
                    padding: 'var(--spacing-2) var(--spacing-3)',
                    background: 'var(--color-surface-muted)',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacing-1)',
                  }}
                >
                  <strong style={{ color: 'var(--color-text-primary)', fontSize: 'var(--font-size-sm)' }}>
                    {t('_index.viewer.label')}
                  </strong>
                  <div>
                    ID:{' '}
                    <span style={{ fontFamily: 'var(--font-family-mono)' }}>
                      {credentials.grafana.viewerUsername}
                    </span>
                  </div>
                  <div>
                    PW:{' '}
                    <span style={{ fontFamily: 'var(--font-family-mono)' }}>
                      {credentials.grafana.viewerPassword || t('_index.viewer.notConfigured')}
                    </span>
                  </div>
                  <small style={{ color: 'var(--color-text-muted)', marginTop: 'var(--spacing-1)' }}>
                    {t('_index.viewer.credentialNotice')}
                  </small>
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>

      <details
        style={{
          marginTop: 'var(--spacing-3)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
        }}
      >
        <summary
          style={{
            cursor: 'pointer',
            userSelect: 'none',
            color: 'var(--color-text-muted)',
            fontWeight: 600,
          }}
        >
          접속 정보
        </summary>
        <div
          style={{
            marginTop: 'var(--spacing-2)',
            padding: 'var(--spacing-3)',
            background: 'var(--color-surface)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-surface-border)',
            lineHeight: 1.7,
            fontStyle: 'italic',
            color: 'var(--color-text-muted)',
          }}
        >
          {t('_index.monitoring.adminGuide')}
        </div>
      </details>
    </div>
  );
}
